import type { Extension } from "@codemirror/state";
import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import {
  allStaticAgentInstructionTileKeys,
  compareStaticSectionValuesAsStored,
  STATIC_AGENT_INSTRUCTION_TILES,
  type StaticAgentInstructionTileKey,
  type StaticTeammateAgentSections,
} from "@repo/app-types/entities/teammateAgent";
import type { TeammateAgentInstructionProposalData } from "@repo/app-types/entities/teammateAgentProposal";
import type {
  AgentInstructionChecksLlmOutput,
  StaticAgentSectionKey,
} from "@repo/app-types/entities/voiceAgentConfigurationTools";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ScrollEdgeFades } from "@repo/ui/chat/scrollEdgeFades";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import {
  IconCircles,
  IconCube3dSphere,
  IconMessageChatbot,
  IconSTurnRight,
  IconUserHexagon,
} from "@tabler/icons-react";
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FC,
  type Ref,
} from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useResolveProposalMutation } from "../../../api/entities/copilotProposal.js";
import { useGetTeammateAgentQuery } from "../../../api/entities/teammateAgent.js";
import { isConflictQueryError } from "../../../api/queryErrors.js";
import type { MarkdownOutlineState } from "../../../components/codeMirror/extensions/markdownOutline.js";
import { CopilotProposalBanner } from "../../../components/copilotProposalBanner.js";
import { DiffableField } from "../../../components/diffableField.js";
import type { ProseEditorHandle } from "../../../components/proseEditor.js";
import { NodeChangeDot } from "../../../components/recordingAssessmentEditor/nodeChangeDot.js";
import { useMarkdownOutline } from "../../../components/useMarkdownOutline.js";
import { useCopilotProps } from "../../copilot/copilotContext.js";
import {
  useDeepLinkedProposal,
  useDeepLinkedTestResult,
} from "../../copilot/copilotDeepLinks.js";
import { buildTestInstructionsPrompt } from "../../copilot/copilotRouteRegistry.js";
import {
  useReportEditorProposal,
  type EditorFocus,
} from "../../copilot/editorProposalsSlice.js";
import { AgentSectionChecksCard } from "./agentSectionChecksCard.js";
import { AvailableResourcesCard } from "./availableResourcesCard.js";
import { InstructionTileRow } from "./instructionTileRow.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

const TILE_ICONS: Record<
  StaticAgentInstructionTileKey,
  ComponentType<{ className?: string }>
> = {
  identity: IconCircles,
  caller_context: IconUserHexagon,
  conversation_flow: IconSTurnRight,
  behavior_and_guardrails: IconCube3dSphere,
  outreach_instructions: IconMessageChatbot,
};

interface TileGroup {
  label: string;
  tileKeys: readonly StaticAgentInstructionTileKey[];
}

const TILE_GROUPS: readonly TileGroup[] = [
  {
    label: "Foundation",
    tileKeys: ["identity", "caller_context", "behavior_and_guardrails"],
  },
  { label: "Flows", tileKeys: ["outreach_instructions", "conversation_flow"] },
];

/** The outline of a tile's editor. */
interface TileEditorOutline extends MarkdownOutlineState {
  tileKey: StaticAgentInstructionTileKey;
}

interface TileEditorOutlines {
  active?: TileEditorOutline;
  /** The last tile's, kept so its row can animate closed. */
  previous?: TileEditorOutline;
}

const OUTREACH_PLACEHOLDER = `How to run the outreach over time. Example:

Check in with customers before their renewal. Be brief and warm.

1. Text first: "<the approved message>". Wait for a reply.
2. If they reply that all is well, thank them and finish.
3. If they need help, text a confirmation, then call them about five minutes later.
4. If there is no reply, finish when the task expires.

Say what to text word for word where it matters, when a call is warranted, and when to stop.`;

const ROLE_PLACEHOLDER =
  "Describe what the agent does, how it should sound, and any persona boundaries it should follow.";
const CALLER_CONTEXT_PLACEHOLDER =
  "A potential customer calling the phone number on our website.";
const CONVERSATION_FLOW_PLACEHOLDER = `Follow this conversation flow unless the caller clearly needs something different.

## 1. Opening
Greet the caller, introduce yourself, and ask how you can help.

## 2. Understand the request
Clarify what the caller is trying to accomplish before giving an answer.

## 3. Resolve or route
Help the caller complete the next step when possible, or transfer/escalate according to the available options.

## 4. Close
Summarize the outcome, confirm any next step, and ask if there is anything else they need.`;
const GUARDRAILS_PLACEHOLDER =
  "Add any general behavior rules and guardrails the agent should always follow.";

// Match the HQ section authoring limits.
const NAME_MAX_LENGTH = 200;
const SECTION_MAX_LENGTH = 125_000;

interface SectionFieldEditorProps {
  label: string;
  /** Optional help text shown in a tooltip on an info icon next to the label. */
  labelTooltip?: string;
  value: string;
  /** Published value to diff against; null when live isn't loaded. */
  liveValue: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  isMultiline: boolean;
  /** Shows the text as styled markdown. */
  isMarkdown?: boolean;
  isReadOnly: boolean;
  fillHeight?: boolean;
  maxLength?: number;
  /**
   * The copilot's proposed replacement for this field, if any. Apply/Discard
   * live on the section-level banner (see `InstructionsTab`).
   */
  suggestion?: string;
  editorRef?: Ref<ProseEditorHandle>;
  editorExtensions?: Extension;
}

/** A labeled instruction-section field that can diff against live or a suggestion. */
const SectionFieldEditor: FC<SectionFieldEditorProps> = (props) => {
  const {
    label,
    labelTooltip,
    value,
    liveValue,
    onChange,
    placeholder,
    isMultiline,
    isMarkdown,
    isReadOnly,
    fillHeight = false,
    maxLength,
    suggestion,
    editorRef,
    editorExtensions,
  } = props;

  // Saved trimmed, so whitespace at either end isn't a change.
  let originalValue = liveValue ?? undefined;
  if (
    originalValue !== undefined &&
    compareStaticSectionValuesAsStored(originalValue, value)
  ) {
    originalValue = value;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col",
        fillHeight ? "min-h-0 flex-1" : "shrink-0",
      )}
    >
      <DiffableField
        label={
          <div className="flex items-center gap-1">
            <Label size={Size.SMALL}>{label}</Label>
            {labelTooltip ? (
              <InfoIconTooltip>
                <span className="block max-w-xs">{labelTooltip}</span>
              </InfoIconTooltip>
            ) : null}
          </div>
        }
        value={value}
        onChange={onChange}
        originalValue={originalValue}
        suggestedValue={suggestion}
        placeholder={placeholder}
        maxLength={maxLength}
        isReadOnly={isReadOnly}
        variant={isMultiline ? "textarea" : "input"}
        isMarkdown={isMarkdown}
        collapseUnchanged
        hasDiffMinimap
        editorClassName={fillHeight ? "min-h-0 flex-1" : undefined}
        editorRef={editorRef}
        editorExtensions={editorExtensions}
      />
    </div>
  );
};

/** The pending instruction proposal the tab shows on its tile. */
interface LinkedInstructionProposal {
  id: CopilotProposalId;
  data: TeammateAgentInstructionProposalData;
}

/** The proposal's new content per section it changes, keyed like the draft. */
function readProposedSections(
  proposal: LinkedInstructionProposal,
): Partial<Record<StaticAgentSectionKey, string>> {
  const proposed: Partial<Record<StaticAgentSectionKey, string>> = {};
  for (const section of STATIC_AGENT_INSTRUCTION_TILES[proposal.data.tile]
    .sections) {
    const change = proposal.data.sections[section];
    if (change) {
      proposed[section] = change.content;
    }
  }
  return proposed;
}

/**
 * The Instructions tab for a static agent: a left sub-navigation over the
 * authored prompt sections, an editor for the selected section, and a
 * read-only summary of attached resources. Section edits autosave to the draft
 * via the shared controller and go live through the toolbar's Publish button.
 *
 * An Ask Alpharun row deep-links here with `?proposalId=` or `?testResultId=`
 * (both one-shot): a pending proposal shows on its tile, read-only, with
 * Apply / Discard, and a test result fills the Results panel.
 */
export const InstructionsTab: FC = () => {
  const {
    teammateAgentId,
    agentConfig,
    flushAgentConfigSave,
    hasUnpublishedChanges,
  } = useStaticAgentConfig();
  const navigate = useNavigate();
  const copilot = useCopilotProps();
  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();

  const [selectedKey, setSelectedKey] =
    useState<StaticAgentInstructionTileKey>("identity");
  const [isOutlineExpanded, setIsOutlineExpanded] = useState(true);
  const [outlines, setOutlines] = useState<TileEditorOutlines>({});
  const editorRef = useRef<ProseEditorHandle>(null);
  const navRef = useRef<HTMLElement>(null);
  const navContentRef = useRef<HTMLDivElement>(null);
  const markdownOutline = useMarkdownOutline();

  // `sections` is a single nested object on the config draft. Until the draft
  // query resolves it is null; editing then would write a mostly-empty object
  // back and wipe the four sections the user didn't touch, so the tab waits
  // for the draft rather than falling back to an empty object.
  const sections = agentConfig.data?.sections ?? null;

  // The proposal the tab is showing, the checks of the last proposal or test
  // result it loaded, and the test result the Results panel is showing.
  const [linkedProposal, setLinkedProposal] =
    useState<LinkedInstructionProposal | null>(null);
  const [checks, setChecks] = useState<AgentInstructionChecksLlmOutput | null>(
    null,
  );
  const [linkedTestResultId, setLinkedTestResultId] = useState<
    ConfigurationTestResultId | undefined
  >();

  const notifyUnavailable = (subject: string) => {
    showToast({
      variant: ToastVariant.Error,
      title: `This ${subject} is no longer available.`,
    });
  };

  // `?proposalId=`: the pending row goes onto its tile as soon as it loads. A
  // row for another agent, or one that is not an instruction proposal, loads
  // nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("teammateAgent", {
      onUnavailable: () => notifyUnavailable("suggestion"),
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (
      deepLinkProposal.resource_id !== teammateAgentId ||
      deepLinkProposal.data.kind !== "update_instruction_section"
    ) {
      return;
    }
    const { data } = deepLinkProposal;
    setLinkedProposal({ id: deepLinkProposal.id, data });
    setLinkedTestResultId(undefined);
    setChecks(data.checks);
    setSelectedKey(data.tile);
    setIsOutlineExpanded(true);
  }, [deepLinkProposal, teammateAgentId, clearDeepLinkProposalId]);

  // `?testResultId=`: the saved checks fill the Results panel.
  const { row: deepLinkTestResult, clear: clearDeepLinkTestResultId } =
    useDeepLinkedTestResult("teammateAgent", {
      onUnavailable: () => notifyUnavailable("test result"),
    });
  useEffect(() => {
    if (!deepLinkTestResult) {
      return;
    }
    clearDeepLinkTestResultId();
    if (deepLinkTestResult.resource_id !== teammateAgentId) {
      return;
    }
    setLinkedTestResultId(deepLinkTestResult.id);
    setChecks(deepLinkTestResult.data.checks);
  }, [deepLinkTestResult, teammateAgentId, clearDeepLinkTestResultId]);

  // Publishing or discarding the draft discards the pending proposals server
  // side and replaces what the checks measured. The flag goes `true → false`
  // only on a successful publish/discard; edits and failed publishes keep it
  // `true`, so this fires exactly on settle.
  const prevHasUnpublishedRef = useRef(hasUnpublishedChanges);
  useEffect(() => {
    const justSettled = prevHasUnpublishedRef.current && !hasUnpublishedChanges;
    prevHasUnpublishedRef.current = hasUnpublishedChanges;
    if (justSettled) {
      setLinkedProposal(null);
      setLinkedTestResultId(undefined);
      setChecks(null);
    }
  }, [hasUnpublishedChanges]);

  // Tell the chat which row this tab is showing and which tile is selected.
  const hasUnsavedChanges = agentConfig.hasUnsavedChanges;
  const editorFocus = useMemo(
    (): EditorFocus => ({
      resource_type: "teammateAgent",
      teammateAgentId,
      tile: selectedKey,
      hasUnsavedChanges,
    }),
    [teammateAgentId, selectedKey, hasUnsavedChanges],
  );
  useReportEditorProposal(
    linkedProposal?.id ?? linkedTestResultId,
    isResolvingProposal,
    editorFocus,
  );

  const resolveLinkedProposal = async (
    proposal: LinkedInstructionProposal,
    status: "applied" | "discarded",
  ) => {
    try {
      await resolveProposal({ proposalId: proposal.id, status }).unwrap();
    } catch (err) {
      if (isConflictQueryError(err)) {
        notifyUnavailable("suggestion");
      } else {
        console.error("Failed to resolve the suggestion:", err);
        showToast({
          variant: ToastVariant.Error,
          title: "Failed to update the suggestion",
          description: "Please try again.",
        });
        return;
      }
    }
    setLinkedProposal((current) => (current === proposal ? null : current));
  };

  // Applying commits the proposed content to the draft, then — once the
  // change has rendered, which the flush reads — persists the draft and
  // records the decision. A failed flush leaves the proposal pending, so the
  // next attempt retries it.
  const [applyingProposal, setApplyingProposal] =
    useState<LinkedInstructionProposal | null>(null);
  const applyLinkedProposal = () => {
    if (!sections || !linkedProposal) {
      return;
    }
    agentConfig.onChange({
      sections: {
        ...sections,
        ...readProposedSections(linkedProposal),
      },
    });
    setApplyingProposal(linkedProposal);
  };
  const finishApplyingProposal = useEffectEvent(
    async (proposal: LinkedInstructionProposal) => {
      const saved = await flushAgentConfigSave();
      setApplyingProposal(null);
      if (saved) {
        await resolveLinkedProposal(proposal, "applied");
      }
    },
  );
  useEffect(() => {
    if (applyingProposal) {
      void finishApplyingProposal(applyingProposal);
    }
  }, [applyingProposal]);

  const discardLinkedProposal = () => {
    if (linkedProposal) {
      void resolveLinkedProposal(linkedProposal, "discarded");
    }
  };

  // The Test button hands off to Ask Alpharun: the saved draft is what its
  // tool checks, and the turn's "Test results" row opens here. Outside the
  // Ask Alpharun shell (stories, tests) there is nothing to hand off to.
  const isAskAlpharunAvailable = copilot !== undefined;
  const [isFlushingForTest, setIsFlushingForTest] = useState(false);
  const runChecks = async () => {
    if (!copilot) {
      return;
    }
    setIsFlushingForTest(true);
    try {
      if (!(await flushAgentConfigSave())) {
        return;
      }
      copilot.onOpenChat();
      copilot.onSendMessage(
        buildTestInstructionsPrompt(teammateAgent?.name ?? "this agent"),
      );
    } finally {
      setIsFlushingForTest(false);
    }
  };

  // The Outreach tile is shown only for an agent that runs its own call tasks.
  const isAgentManaged =
    agentConfig.data?.settings.calling?.agent_managed ?? false;
  const navTileKeys = allStaticAgentInstructionTileKeys.filter(
    (tileKey) => tileKey !== "outreach_instructions" || isAgentManaged,
  );
  const activeKey = navTileKeys.includes(selectedKey)
    ? selectedKey
    : "identity";
  const activeTile = STATIC_AGENT_INSTRUCTION_TILES[activeKey];
  const outlineExtension = useMemo(
    () =>
      markdownOutline?.((state) =>
        setOutlines(({ active, previous }) => ({
          active: { ...state, tileKey: activeKey },
          previous: active?.tileKey === activeKey ? previous : active,
        })),
      ),
    [markdownOutline, activeKey],
  );

  // The fields' editors must mount with the draft's text: one that mounts
  // empty would take the draft's arrival as an edit that undo can empty.
  if (sections === null) {
    return (
      <div className="flex h-full items-center justify-center bg-card">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  }

  // A section is "in a draft state" when its current value differs from the
  // published (live) value — the same unpublished-change signal the playbook
  // editor shows on its section tiles.
  const liveSections = agentConfig.liveData?.sections ?? null;
  const hasUnpublishedChange = (
    fields: readonly (keyof StaticTeammateAgentSections)[],
  ) =>
    liveSections !== null &&
    fields.some(
      (field) =>
        !compareStaticSectionValuesAsStored(
          sections[field] ?? "",
          liveSections[field] ?? "",
        ),
    );

  const suggestions = linkedProposal
    ? readProposedSections(linkedProposal)
    : {};

  const updateSection = (
    key: keyof StaticTeammateAgentSections,
    value: string,
  ) => {
    agentConfig.onChange({ sections: { ...sections, [key]: value } });
  };

  // Clicking the active tile shows or hides its outline.
  const pressTile = (tileKey: StaticAgentInstructionTileKey) => {
    if (tileKey === activeKey) {
      setIsOutlineExpanded((current) => !current);
      return;
    }
    setSelectedKey(tileKey);
    setIsOutlineExpanded(true);
  };

  // The suggestion banner wraps the whole active tile (matching the playbook
  // editor), so Apply/Discard act on the proposal's every section.
  const activeSectionHasSuggestion = linkedProposal?.data.tile === activeKey;

  // While a section has a pending suggestion, the whole section is a read-only
  // review view (matching the playbook editor's `isReadOnly={isSuggestionMode}`):
  // every field is disabled and the suggested one shows the suggested text.
  const fieldsReadOnly = activeSectionHasSuggestion;

  return (
    <div className="flex h-full min-w-0 gap-2 bg-card p-2">
      <div className="flex w-[280px] shrink-0 flex-col overflow-y-auto">
        <div className="relative flex min-h-0 flex-1 flex-col">
          <ScrollEdgeFades
            scrollContainerRef={navRef}
            scrollContentRef={navContentRef}
          />
          <nav
            ref={navRef}
            aria-label="Sections"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <div ref={navContentRef} className="space-y-4">
              <h2 className="px-2.5 pt-2 text-base font-semibold text-foreground">
                Sections
              </h2>
              {TILE_GROUPS.map((group) => (
                <div key={group.label} className="flex flex-col gap-1">
                  <h3 className="px-2.5 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </h3>
                  <ul>
                    {group.tileKeys
                      .filter((tileKey) => navTileKeys.includes(tileKey))
                      .map((tileKey) => {
                        const tile = STATIC_AGENT_INSTRUCTION_TILES[tileKey];
                        const isActive = tileKey === activeKey;
                        const ItemIcon = TILE_ICONS[tileKey];
                        // A tile's old outline is only for closing its row. Once
                        // the tile opens again, its text may have changed.
                        let outline: TileEditorOutline | undefined;
                        if (outlines.active?.tileKey === tileKey) {
                          outline = outlines.active;
                        } else if (
                          !isActive &&
                          outlines.previous?.tileKey === tileKey
                        ) {
                          outline = outlines.previous;
                        }
                        return (
                          <InstructionTileRow
                            key={tileKey}
                            icon={<ItemIcon className="size-3.5" />}
                            title={
                              <span className="flex items-center gap-2">
                                {tile.label}
                                {linkedProposal?.data.tile === tileKey ? (
                                  <NodeChangeDot
                                    entity="section"
                                    isVisible
                                    variant="suggestion"
                                  />
                                ) : (
                                  <NodeChangeDot
                                    entity="section"
                                    isVisible={hasUnpublishedChange(
                                      tile.sections,
                                    )}
                                  />
                                )}
                              </span>
                            }
                            outline={outline}
                            isActive={isActive}
                            isExpanded={isOutlineExpanded}
                            onPress={() => pressTile(tileKey)}
                            onHeadingPress={(lineNumber) =>
                              editorRef.current?.scrollToLine(lineNumber)
                            }
                          />
                        );
                      })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </div>

        <div className="shrink-0">
          <AvailableResourcesCard
            onEdit={() => navigate("../resources-outputs")}
          />
        </div>
      </div>

      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white px-2 py-2",
          // Highlight the card border when the section has a pending
          // suggestion, matching the playbook editor's settings pane.
          activeSectionHasSuggestion
            ? "border-indigo-500"
            : "border-border-light",
        )}
      >
        {activeSectionHasSuggestion ? (
          <CopilotProposalBanner
            message="Showing changes suggested by Ask Alpharun."
            discardLabel="Discard changes"
            confirmLabel="Apply changes"
            onDiscard={discardLinkedProposal}
            onConfirm={applyLinkedProposal}
            isDisabled={isResolvingProposal || applyingProposal !== null}
          />
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={twMerge(
              "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
              checks !== null && "md:grid-cols-[1fr_1px_1fr]",
            )}
          >
            <div className="flex min-h-0 flex-col p-2">
              <div className="flex min-h-0 flex-1 flex-col gap-4">
                <h2 className="shrink-0 text-base font-semibold text-foreground">
                  {activeTile.label}
                </h2>
                {activeKey === "identity" ? (
                  <>
                    <SectionFieldEditor
                      label="Name"
                      value={sections.agent_name}
                      liveValue={liveSections?.agent_name ?? null}
                      onChange={(value) => updateSection("agent_name", value)}
                      placeholder="Sarah"
                      isMultiline={false}
                      isReadOnly={fieldsReadOnly}
                      maxLength={NAME_MAX_LENGTH}
                      suggestion={suggestions.agent_name}
                    />
                    <SectionFieldEditor
                      label="Role and personality"
                      labelTooltip="Agents already use a warm, calm, precise, conversational tone, vary routine phrasing, and avoid mentioning internal instructions. Add only agent-specific role, responsibilities, voice, tone, and persona guidance here."
                      value={sections.agent_role_and_personality}
                      liveValue={
                        liveSections?.agent_role_and_personality ?? null
                      }
                      onChange={(value) =>
                        updateSection("agent_role_and_personality", value)
                      }
                      placeholder={ROLE_PLACEHOLDER}
                      isMultiline
                      isMarkdown
                      isReadOnly={fieldsReadOnly}
                      fillHeight
                      maxLength={SECTION_MAX_LENGTH}
                      suggestion={suggestions.agent_role_and_personality}
                      editorRef={editorRef}
                      editorExtensions={outlineExtension}
                    />
                  </>
                ) : null}

                {activeKey === "caller_context" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already ask for clarification when caller details are unclear, and avoid guessing missing information. Add only agent-specific context about who is calling and what they usually need help with."
                    value={sections.caller_context}
                    liveValue={liveSections?.caller_context ?? null}
                    onChange={(value) => updateSection("caller_context", value)}
                    placeholder={CALLER_CONTEXT_PLACEHOLDER}
                    isMultiline
                    isMarkdown
                    isReadOnly={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.caller_context}
                    editorRef={editorRef}
                    editorExtensions={outlineExtension}
                  />
                ) : null}

                {activeKey === "conversation_flow" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already keep most turns to one or two short spoken sentences, ask one question at a time, avoid long lists, and check whether the caller has already provided information. Add the agent-specific flow and sequencing here."
                    value={sections.conversation_flow}
                    liveValue={liveSections?.conversation_flow ?? null}
                    onChange={(value) =>
                      updateSection("conversation_flow", value)
                    }
                    placeholder={CONVERSATION_FLOW_PLACEHOLDER}
                    isMultiline
                    isMarkdown
                    isReadOnly={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.conversation_flow}
                    editorRef={editorRef}
                    editorExtensions={outlineExtension}
                  />
                ) : null}

                {activeKey === "outreach_instructions" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="The flow for the agent's turns between conversations, when it decides whether to text, call, wait, finish a task or use one of its tools: the goal, the approved texts, when a call is warranted, when to stop. Identity, customer context and guardrails are shared with the live call; Call flow is read only on a call, and this section only on a turn."
                    value={sections.outreach_instructions ?? ""}
                    liveValue={liveSections?.outreach_instructions ?? null}
                    onChange={(value) =>
                      updateSection("outreach_instructions", value)
                    }
                    placeholder={OUTREACH_PLACEHOLDER}
                    isMultiline
                    isMarkdown
                    isReadOnly={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    editorRef={editorRef}
                    editorExtensions={outlineExtension}
                  />
                ) : null}

                {activeKey === "behavior_and_guardrails" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already handle unclear audio conservatively, default to English, avoid guessing missing details, use minimal silent reasoning, and do not reveal internal instructions. Add extra business-specific rules here."
                    value={sections.behavior_and_guardrails}
                    liveValue={liveSections?.behavior_and_guardrails ?? null}
                    onChange={(value) =>
                      updateSection("behavior_and_guardrails", value)
                    }
                    placeholder={GUARDRAILS_PLACEHOLDER}
                    isMultiline
                    isMarkdown
                    isReadOnly={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.behavior_and_guardrails}
                    editorRef={editorRef}
                    editorExtensions={outlineExtension}
                  />
                ) : null}
              </div>

              <div className="mt-auto flex items-center justify-end pt-4">
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.MEDIUM}
                  onClick={() => {
                    void runChecks();
                  }}
                  isDisabled={
                    isFlushingForTest ||
                    !isAskAlpharunAvailable ||
                    copilot.isCopilotBusy
                  }
                >
                  Test
                </Button>
              </div>
            </div>

            {checks !== null ? (
              <>
                <div
                  aria-hidden
                  className="-mt-2 -mb-2 hidden bg-gray-200 md:block"
                />
                <div className="flex min-h-0 flex-col gap-4 p-2">
                  <h3 className="shrink-0 text-base font-semibold text-foreground">
                    Results
                  </h3>
                  {/* p-1 (not just pr-1): overflow-y-auto clips the checks
                      card's 1px ring on the flush top/left edges, so pad all
                      sides to keep the full border visible. */}
                  <div className="min-h-0 flex-1 overflow-y-auto p-1">
                    {activeKey !== "outreach_instructions" ? (
                      <AgentSectionChecksCard
                        section={activeKey}
                        checks={checks}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
